import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CopilotInsightView } from '../../models/copilot-insight.model';
import { SocketService } from '../socket/socket.service';
import { AttendanceCopilotApiService } from './attendance-copilot-api.service';
import { CopilotSocketPayload } from './attendance-copilot-socket.service';

export interface CopilotInsightUiState {
  idAttendance: string | null;
  view: CopilotInsightView | null;
  /** Messages since the insight: the server's figure on every fetch, plus the ones handed over live (RF-41). */
  newMessages: number;
  /** A request of this screen is on its way to the API. */
  requesting: boolean;
  /** The generation this screen asked for, while it runs (RF-32). */
  requestedGenerationId: string | null;
  /** The generation this screen asked for failed; the notice is for this user only (RF-32). */
  requestFailed: boolean;
}

const INITIAL_STATE: CopilotInsightUiState = {
  idAttendance: null,
  view: null,
  newMessages: 0,
  requesting: false,
  requestedGenerationId: null,
  requestFailed: false,
};

const INSIGHT_SIGNAL_EVENT = 'copilot:insight';

/**
 * The insight of the open conversation, above the tabs (SPEC-2026-10-copilot-attendant-insight,
 * plan AD-13): one GET when the conversation opens, one more on every `copilot:insight` signal,
 * and never on a timer (RF-04). The shape follows `AttendanceCopilotStateService`.
 */
@Injectable({ providedIn: 'root' })
export class AttendanceCopilotInsightService {
  private readonly state$ = new BehaviorSubject<CopilotInsightUiState>({ ...INITIAL_STATE });
  private signals: Subscription | null = null;
  private room: string | null = null;

  constructor(
    private readonly socketService: SocketService,
    private readonly apiService: AttendanceCopilotApiService,
  ) { }

  get snapshot(): CopilotInsightUiState {
    return this.state$.getValue();
  }

  select(): Observable<CopilotInsightUiState> {
    return this.state$.asObservable();
  }

  /** Follows a conversation: joins its Copilot room and reads its insight. */
  open(idAttendance: string): void {
    if (this.snapshot.idAttendance === idAttendance) return;
    this.close();
    this.state$.next({ ...INITIAL_STATE, idAttendance });
    this.room = `copilot_attendance_${idAttendance}`;
    this.socketService.joinEvent(this.room);
    this.signals = this.socketService
      .listen(INSIGHT_SIGNAL_EVENT)
      .subscribe((payload: CopilotSocketPayload) => this.onSignal(payload));
    this.fetch(idAttendance);
  }

  /** Stops following the conversation. */
  close(): void {
    this.signals?.unsubscribe();
    this.signals = null;
    if (this.room) this.socketService.leaveEvent(this.room);
    this.room = null;
    this.state$.next({ ...INITIAL_STATE });
  }

  /** A message appended to the open conversation; every message counts (RF-09, RF-41). */
  noteMessage(): void {
    this.patch({ newMessages: this.snapshot.newMessages + 1 });
  }

  /** Asks for a generation (RF-30, RF-31). The API refuses what the screen should not offer (RNF-07). */
  generate(): void {
    const { idAttendance, requesting } = this.snapshot;
    if (!idAttendance || requesting) return;
    this.patch({ requesting: true, requestFailed: false });
    this.apiService.generateInsight(idAttendance).subscribe({
      next: (res) => this.apply(idAttendance, res.data, res.data.generation?.id ?? null),
      error: () => this.fetch(idAttendance),
    });
  }

  private fetch(idAttendance: string): void {
    this.apiService.getInsight(idAttendance).subscribe({
      next: (res) => this.apply(idAttendance, res.data, this.snapshot.requestedGenerationId),
      error: () => this.patch({ requesting: false }),
    });
  }

  // A response of a conversation no longer open is dropped; the requested id is kept while its generation runs.
  private apply(idAttendance: string, view: CopilotInsightView, requestedGenerationId: string | null): void {
    if (this.snapshot.idAttendance !== idAttendance) return;
    const running = view.generation?.id === requestedGenerationId ? requestedGenerationId : null;
    this.patch({ view, newMessages: view.newMessages, requesting: false, requestedGenerationId: running });
  }

  // A signal carries no content (plan AD-06): the state is read again from the API. The socket stays
  // in every conversation room it joined (the gateway has no leave), so only this one's signals count.
  private onSignal(payload: CopilotSocketPayload): void {
    const { idAttendance, requestedGenerationId } = this.snapshot;
    if (!idAttendance || payload?.conversationId !== idAttendance) return;
    if (this.isOwnFailure(payload, requestedGenerationId)) this.patch({ requestFailed: true, requestedGenerationId: null });
    this.fetch(idAttendance);
  }

  private isOwnFailure(payload: CopilotSocketPayload, requestedGenerationId: string | null): boolean {
    return !!requestedGenerationId && payload?.requestId === requestedGenerationId && payload?.result?.['state'] === 'failed';
  }

  private patch(partial: Partial<CopilotInsightUiState>): void {
    this.state$.next({ ...this.snapshot, ...partial });
  }
}
